import { computed, toJS, observable } from 'mobx';
import { BaseStore, DataStore } from '.';
import { Stores } from '../models/Stores';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import getQueryResults from '../utils/query/getQueryResults';
import { RatingImageOptions } from '../models/configuration/components/RatingImageOptions';
import getDataPoint from '../utils/getDataPoint';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { getDisclaimers } from '../utils/disclaimers';
import { TRatingImagePosition } from '../models/configuration/components/TRatingImagePosition';

type ImageSourceType = undefined | 'default' | 'target';

export class RatingImageStore extends BaseStore {
    dataStore: DataStore;
    @observable.ref
    private shareClass;
    @observable
    private fileExists: boolean = false;
    private existingImageType: ImageSourceType;

    constructor(stores: Stores, shareClass: any) {
        super(stores);
        this.dataStore = stores.data;
        // TODO: Setup entityStore, update references
        this.shareClass = shareClass;
        this.initialize();
    }

    /**
     * Determine if we have any images available for use, and indicate a selected image source for display based on order of priority and their existence.
     */
    initialize() {
        if (this.hasRatingImageOptions) {
            this.determineImageExistence(this.imageFileNameUrl, 'target');
            if (!this.fileExists) {
                this.determineImageExistence(
                    this.defaultImageFileNameUrl,
                    'default'
                );
            }
        }
    }

    @computed
    get ratingImagePosition(): TRatingImagePosition {
        const defaultPosition: TRatingImagePosition = 'aboveFundManager';
        if (this.hasRatingImageOptions) {
            const { position } = this.ratingImageOptionsConfig;
            return position || defaultPosition;
        }
        return defaultPosition;
    }

    /**
     * Used by the FundCard to determine if we need to show the right-side column
     *
     * @readonly
     */
    @computed
    get showComponent() {
        return !this.hideComponent;
    }

    /**
     * The ratings image should be hidden if it has no image or image placeholder to display, when noData configuration indicates so
     *
     * @readonly
     */
    @computed
    get hideComponent() {
        if (this.hasRatingImageOptions) {
            const { noData } = this.ratingImageOptionsConfig;
            if (
                noData &&
                noData.hideComponent &&
                !this.fileExists &&
                !this.noImagePlaceholder
            ) {
                return true;
            }
            return false;
        }
        // hide component by default
        return true;
    }

    @computed
    get queryContext(): IQueryContext {
        return {
            entityByType: {
                [this.dataStore.config.entityType]: this.shareClass,
            },
            globalDisclaimers: toJS(this.dataStore.rawDisclaimers as any),
        };
    }

    @computed
    get hasRatingImageOptions(): boolean {
        return this.ratingImageOptionsConfig !== undefined;
    }

    @computed
    get ratingImageOptionsConfig(): RatingImageOptions {
        const { ratingImageOptions } = this.dataStore.config;
        return ratingImageOptions;
    }

    @computed
    get hasHeadingText(): boolean {
        const { headingTextQuery } = this.ratingImageOptionsConfig;
        return headingTextQuery !== undefined;
    }

    @computed
    get headingText(): string {
        const { headingTextQuery } = this.ratingImageOptionsConfig;
        const result = getQueryResults(
            this.queryContext,
            { headingTextQuery },
            this.dataStore.culture,
            this.dataStore.appDataProps
        );
        return result.headingTextQuery;
    }

    @computed
    get hasAsAtDate(): boolean {
        const { asAtDate } = this.ratingImageOptionsConfig;
        return asAtDate !== undefined;
    }

    // Uses old school AsAtDate to avoid confusion when setting up config
    @computed
    get asAtDate() {
        const { asAtDate } = this.ratingImageOptionsConfig;
        if (this.hasAsAtDate) {
            const { label, data, format } = asAtDate;
            const value = getDataPoint({
                dataStore: this.dataStore,
                dataConfig: { ...data, format },
                shareClass: this.shareClass,
                locale: this.dataStore.culture,
                fundCurrency: this.shareClass.fundCurrency,
                defaultNoDataPlaceholder: '-',
                hasPerformance: this.shareClass.hasPerformance,
            });

            return {
                label,
                value,
            };
        }

        return null;
    }

    @computed
    get imageFileName(): string {
        const { imageQuery } = this.ratingImageOptionsConfig;
        const result = getQueryResults(
            this.queryContext,
            { imageQuery },
            this.dataStore.culture,
            this.dataStore.appDataProps
        );
        return result.imageQuery;
    }

    @computed
    get imageFileNameUrl(): string {
        return this.getImageUrl(this.imageFileName);
    }

    @computed
    get defaultImageFileName(): string | undefined {
        const { noImage } = this.ratingImageOptionsConfig;
        if (noImage && noImage.defaultImageQuery) {
            const { defaultImageQuery } = noImage;
            const result = getQueryResults(
                this.queryContext,
                { defaultImageQuery },
                this.dataStore.culture,
                this.dataStore.appDataProps
            );
            return result.defaultImageQuery;
        }
    }

    @computed
    get defaultImageFileNameUrl(): string {
        return this.getImageUrl(this.defaultImageFileName);
    }

    @computed
    get disclaimers() {
        const { disclaimers } = this.ratingImageOptionsConfig;
        if (disclaimers) {
            return getDisclaimers(
                this.dataStore,
                disclaimers,
                this.shareClass.disclaimers,
                this.queryContext
            );
        }
    }

    @computed
    get noDataPlaceholder() {
        const { noData } = this.ratingImageOptionsConfig;
        if (noData && noData.placeholder) {
            return noData.placeholder;
        }
    }

    @computed
    get noImagePlaceholder() {
        const { noImage } = this.ratingImageOptionsConfig;
        if (noImage && noImage.placeholder) {
            return noImage.placeholder;
        }
    }

    /**
     * Used to track if either the target image or default image files exist, based on the results from the initialize method.
     *
     * @readonly
     */
    @computed
    get imageFileExists() {
        return this.fileExists;
    }

    @computed
    get targetImageExists() {
        return this.fileExists && this.existingImageType === 'target';
    }

    @computed
    get defaultImageExists() {
        return this.fileExists && this.existingImageType === 'default';
    }

    /**
     * Returns the URL & filename of the image selected for display
     *
     * @readonly
     */
    @computed
    get existingImage(): { url: string; name: string } | undefined {
        if (this.fileExists) {
            if (this.existingImageType === 'target') {
                return { url: this.imageFileNameUrl, name: this.imageFileName };
            } else if (this.existingImageType === 'default') {
                return {
                    url: this.defaultImageFileNameUrl,
                    name: this.defaultImageFileName,
                };
            }
        }
        return;
    }

    /**
     * Execute an http request to fetch the image, caching it in the browser, and determine if the image exists based on its status code.
     * @param imageUrl string
     */
    private determineImageExistence = (
        imageUrl: string,
        imageType: ImageSourceType
    ) => {
        if (!isNullOrEmpty(imageUrl)) {
            const http = new XMLHttpRequest();
            http.open('HEAD', imageUrl, true);
            http.send();
            const exists = http.status < 400;
            this.fileExists = exists;
            if (exists) {
                this.existingImageType = imageType;
            }
        }
    };

    /**
     * Gets the built up asset url for the image file name provided, if the filename is valid.
     * @param imageName string | undefined
     */
    private getImageUrl(imageName: string | undefined) {
        if (!isNullOrEmpty(imageName)) {
            return this.dataStore.assetStore.getAssetUrl(imageName);
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/RatingImageStore.ts